import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import { AlertCircle, ClipboardList, Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { useGraphQL } from '../hooks/useGraphQL.js';
import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

/* ── Types ── */

type OrderStatus = 'pending' | 'in-progress' | 'completed' | 'cancelled';
type FilterStatus = 'All' | OrderStatus;

interface ServiceOrder {
  id: string;
  orderNumber: string;
  description: string;
  status: OrderStatus;
  serviceDate: string;
  location: string;
  serviceType: string;
  technician: string;
}

/* ── GraphQL query ── */

const GET_SERVICE_ORDERS = `
  query GetServiceOrders($first: Int, $status: String, $clientCode: String) {
    serviceOrders(first: $first, status: $status, clientCode: $clientCode) {
      totalCount
      edges {
        node {
          accessKey
          ticketno
          description
          status
          priority
          assignedTo
          clientCode
          createdDate
          dueDate
          completedDate
          client {
            clientCode
            clientName
            city
            state
          }
        }
      }
    }
  }
`;

/* ── GraphQL response shape ── */

interface ServiceOrderNode {
  accessKey: number;
  ticketno: string;
  description: string;
  status: string;
  priority: string;
  assignedTo: string;
  clientCode: string;
  createdDate: string;
  dueDate: string | null;
  completedDate: string | null;
  client: {
    clientCode: string;
    clientName: string;
    city: string;
    state: string;
  } | null;
}

interface ServiceOrdersData {
  serviceOrders: {
    totalCount: number;
    edges: { node: ServiceOrderNode }[];
  };
}

/* ── Data mapping ── */

function normaliseStatus(raw: string): OrderStatus {
  const lower = raw.toLowerCase();
  if (lower === 'in-progress' || lower === 'inprogress' || lower === 'in progress') {
    return 'in-progress';
  }
  if (lower === 'completed' || lower === 'complete') {
    return 'completed';
  }
  if (lower === 'cancelled' || lower === 'canceled') {
    return 'cancelled';
  }
  return 'pending';
}

function mapNode(node: ServiceOrderNode): ServiceOrder {
  const location =
    node.client !== null ? `${node.client.city}, ${node.client.state}` : node.clientCode;

  return {
    id: String(node.accessKey),
    orderNumber: node.ticketno,
    description: node.description,
    status: normaliseStatus(node.status),
    serviceDate: node.dueDate ?? node.createdDate,
    location,
    serviceType: node.description,
    technician: node.assignedTo,
  };
}

/* ── Status config ── */

type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

const statusBadgeVariant: Record<OrderStatus, GlassBadgeVariant> = {
  pending: 'warning',
  'in-progress': 'info',
  completed: 'success',
  cancelled: 'danger',
};

const statusLabel: Record<OrderStatus, string> = {
  pending: 'Pending',
  'in-progress': 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const filterOptions: readonly { value: string; label: string }[] = [
  { value: 'All', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/* ── Mobile card ── */

interface OrderCardProps {
  order: ServiceOrder;
  onView: (id: string) => void;
}

function OrderCard({ order, onView }: OrderCardProps): ReactElement {
  return (
    <GlassCard
      hover
      className="cursor-pointer"
      onClick={() => {
        onView(order.id);
      }}
    >
      <GlassCardContent className="py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-mono text-xs font-semibold text-primary">
                {order.orderNumber}
              </span>
              <GlassBadge variant={statusBadgeVariant[order.status]}>
                {statusLabel[order.status]}
              </GlassBadge>
            </div>
            <p className="text-sm text-text font-medium leading-snug mb-1">{order.description}</p>
            <p className="text-xs text-text-secondary">{order.serviceType}</p>
            <p className="text-xs text-text-tertiary mt-0.5">{order.location}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-text-tertiary whitespace-nowrap">{order.serviceDate}</p>
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Page ── */

export function ServiceOrdersPage(): ReactElement {
  const navigate = useNavigate();
  const user = usePortalAuth((state) => state.user);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('All');

  const { data, loading, error, refetch } = useGraphQL<ServiceOrdersData>(GET_SERVICE_ORDERS, {
    first: 100,
    clientCode: user?.clientCode ?? undefined,
  });

  const allOrders: ServiceOrder[] =
    data?.serviceOrders.edges.map((edge) => mapNode(edge.node)) ?? [];

  const filtered = allOrders.filter((order) => {
    const matchesStatus = statusFilter === 'All' || order.status === statusFilter;
    const query = search.toLowerCase();
    const matchesSearch =
      query === '' ||
      order.orderNumber.toLowerCase().includes(query) ||
      order.description.toLowerCase().includes(query) ||
      order.serviceType.toLowerCase().includes(query) ||
      order.location.toLowerCase().includes(query) ||
      order.technician.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  function handleView(id: string): void {
    void navigate(`/orders/${id}`);
  }

  const clientLabel = user?.clientName ?? 'your account';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-semibold text-text">My Service Orders</h1>
        <p className="text-sm text-text-secondary mt-0.5">
          {loading
            ? 'Loading orders\u2026'
            : `${String(data?.serviceOrders.totalCount ?? allOrders.length)} orders for ${clientLabel}`}
        </p>
      </div>

      {/* Error banner */}
      {error !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to load service orders</p>
            <p className="text-xs mt-0.5 text-danger/80">{error}</p>
          </div>
          <GlassButton variant="ghost" size="sm" onClick={refetch} className="shrink-0 text-danger">
            Retry
          </GlassButton>
        </div>
      )}

      {/* Search / filter bar */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">
              All Orders
              <span className="ml-2 text-sm font-normal text-text-secondary">
                {loading ? '' : `(${String(filtered.length)} shown)`}
              </span>
            </h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
                />
                <GlassInput
                  placeholder="Search orders..."
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearch(e.target.value);
                  }}
                  className="pl-8 w-full sm:w-56"
                  aria-label="Search service orders"
                />
              </div>
              <GlassSelect
                options={filterOptions}
                value={statusFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setStatusFilter(e.target.value as FilterStatus);
                }}
                aria-label="Filter by status"
                className="w-full sm:w-40"
              />
            </div>
          </div>
        </GlassCardHeader>

        {/* Loading state */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={32} className="text-text-tertiary animate-spin" />
            <p className="text-sm text-text-secondary">Loading service orders&hellip;</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <ClipboardList size={40} className="text-text-tertiary" />
            <div className="text-center">
              <p className="text-sm font-medium text-text">No orders found</p>
              <p className="text-xs text-text-secondary mt-1">
                {search !== '' || statusFilter !== 'All'
                  ? 'Try adjusting your search or filter.'
                  : 'No service orders have been created yet.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Table — desktop */}
            <div className="hidden sm:block">
              <GlassTable>
                <GlassTableHead>
                  <GlassTableRow>
                    <GlassTableHeaderCell className="whitespace-nowrap">Order</GlassTableHeaderCell>
                    <GlassTableHeaderCell>Description</GlassTableHeaderCell>
                    <GlassTableHeaderCell className="whitespace-nowrap">
                      Status
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="whitespace-nowrap hidden lg:table-cell">
                      Service Date
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden xl:table-cell">
                      Location
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell aria-label="Actions" />
                  </GlassTableRow>
                </GlassTableHead>
                <GlassTableBody>
                  {filtered.map((order) => (
                    <GlassTableRow
                      key={order.id}
                      className="cursor-pointer"
                      onClick={() => {
                        handleView(order.id);
                      }}
                    >
                      <GlassTableCell>
                        <span className="font-mono text-xs font-semibold text-primary">
                          {order.orderNumber}
                        </span>
                        <p className="text-xs text-text-tertiary mt-0.5">{order.serviceType}</p>
                      </GlassTableCell>
                      <GlassTableCell>
                        <p className="text-text leading-snug line-clamp-2">{order.description}</p>
                      </GlassTableCell>
                      <GlassTableCell>
                        <GlassBadge variant={statusBadgeVariant[order.status]}>
                          {statusLabel[order.status]}
                        </GlassBadge>
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary whitespace-nowrap hidden lg:table-cell">
                        {order.serviceDate}
                      </GlassTableCell>
                      <GlassTableCell className="text-text-secondary hidden xl:table-cell">
                        <span className="line-clamp-1">{order.location}</span>
                      </GlassTableCell>
                      <GlassTableCell className="text-right">
                        <GlassButton
                          variant="ghost"
                          size="sm"
                          aria-label={`View order ${order.orderNumber}`}
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            handleView(order.id);
                          }}
                        >
                          View
                        </GlassButton>
                      </GlassTableCell>
                    </GlassTableRow>
                  ))}
                </GlassTableBody>
              </GlassTable>
            </div>

            {/* Cards — mobile */}
            <div className="sm:hidden p-4 space-y-3">
              {filtered.map((order) => (
                <OrderCard key={order.id} order={order} onView={handleView} />
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-[var(--glass-border-subtle)] flex items-center justify-between">
            <p className="text-xs text-text-tertiary">
              Showing {String(filtered.length)} of {String(allOrders.length)} orders
            </p>
            <GlassBadge variant="default">
              {statusFilter === 'All' ? 'All statuses' : statusLabel[statusFilter]}
            </GlassBadge>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
